import { readBrowserOnboardingWorkspaceReturn, assertInstallWorkspace, isFullPageShopifyReturn, retireCompletedOnboardingInstall } from '@/flows/shopifyOnboardingInstall';
import { redactShopifyInstallTelemetry } from "@/lib/shopifyInstallTelemetry";
import { authService, type ProductWorkspace } from '@common/api/authService';
import { featureService, type WorkspaceCapabilityResponse } from '@common/api/featureService';
import {
    getCurrentProductCode,
    getProductScopedStorageKey,
    isPluginProduct,
} from '@common/api/productContext';
import { useUserStorage } from '@common/hooks/useUserStorage';
import {
    useCallback, useEffect, useRef, useState
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    clearPaymentReturnWorkspaceId,
    readPaymentReturnWorkspaceId,
} from '@/utils/paymentReturnWorkspace';
import { stripAdminBasename } from '@/utils/routerBasename';
import { navigateToOnboardingRoute } from '@/components/Onboarding/inviteRedirect';
import {
    isExpiredWorkspaceTrialSubscription,
    isWorkspaceSubscriptionTrial,
    pickLatestWorkspaceSubscription,
} from './workspaceSubscriptionUtils';
import {
    getPendingWorkspaceReturnId,
    isSuccessfulWorkspaceReturn,
    shouldPreserveWorkspaceReturnParamsForPaymentResult,
    shouldRetryMissingWorkspaceAfterOnboardingReturn,
} from './workspaceReturnUtils';
import {
    resolvePostRefreshWorkspaceSelection,
    shouldRedirectToOnboardingAfterRefresh,
    shouldSuppressEmptyWorkspaceOnboardingRedirect,
} from './workspaceRefreshBehavior';
import {
    hasPendingPasswordSetup,
    isAuthPasswordSetupRoute,
} from '@/utils/pendingPasswordSetup';
import {
    buildShopifyOnboardingResumeRoute,
    getShopifyOnboardingResumeDestination,
    normalizeShopifyOnboardingResumeWorkspace,
    shouldKeepShopifyOnboardingResumeInactive,
    shouldResolveShopifyOnboardingResume,
} from './shopifyOnboardingResume';
import {
    applyPaymentReturnPlanToWorkspace,
    readPaymentReturnPlanOverride,
    shouldApplyPaymentReturnPlanOverride,
} from './paymentReturnPlanSync';
import {
    normalizeProductWorkspaceSubscription,
} from './productWorkspaceSubscription';

const ONBOARDING_RETURN_MAX_ATTEMPTS = 5;
const ONBOARDING_RETURN_RETRY_DELAY_MS = 2000;
const MISSING_WORKSPACE_RETURN_MAX_REFRESH_ATTEMPTS = 3;
const MISSING_WORKSPACE_RETURN_RETRY_DELAY_MS = 1500;

interface MissingWorkspaceReturnRefreshState {
    workspaceId: string;
    attempts: number;
    inFlight: boolean;
}

const wait = (delayMs: number) => new Promise((resolve) => {
    window.setTimeout(resolve, delayMs);
});

function getRequestedWorkspaceIdFromSearchParams(searchParams: URLSearchParams): string | null {
    return searchParams.get('workspace_id')?.trim()
        || searchParams.get('workspaceId')?.trim()
        || null;
}

export type WorkspaceRole = 'OWNER' | 'ADMIN' | 'MEMBER';
export type MemberStatus = 'ACTIVE' | 'INVITED' | 'DECLINED' | 'LEFT';

export interface WorkspaceSubscription {
    id: string;
    plan_id: string;
    status: string;
    is_in_trial?: boolean | null;
    current_period_end: string | null;
    trial_end_at: string | null;
    cancel_at_period_end: boolean;
    created_at?: string | null;
    provider?: string | null;
    // Plan info from benefit_plan
    plan_code: string;
    plan_name: string;
    plan_tier: string;
    billing_cycle: string;
    plan_label?: string;
    plan_badge?: string;
    status_label?: string;
    status_message?: string;
    action_label?: string;
    next_action?: string;
}

export interface Workspace {
    id: string;
    name: string;
    logo_url?: string;
    owner_id: string;
    settings?: Record<string, any>;
    status: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED'; // 根据 user input 推断，或者保持 string
    created_at: string;
    updated_at: string;
    role?: WorkspaceRole; // membership/ownership 角色
    membershipRole?: WorkspaceRole;
    workspaceRole?: string | null;
    workspaceRoleKey?: string | null;
    workspaceRoleName?: string | null;
    subscription?: WorkspaceSubscription | null; // 附加订阅信息
}

export interface UseWorkspacesReturn {
    workspaces: Workspace[];
    currentWorkspace: Workspace | null;
    isLoading: boolean;
    error: Error | null;
    refreshWorkspaces: (options?: RefreshWorkspaceOptions) => Promise<void>;
    switchWorkspace: (workspaceId: string, options?: SwitchWorkspaceOptions | Record<string, string>) => void;
    setCurrentWorkspace: (workspaceId: string | null) => void;
    applyWorkspaceSubscriptionPlan: (
        workspaceId: string,
        planCode?: string | null,
        planName?: string | null,
        billingCycle?: string | null,
    ) => void;
}

export interface SwitchWorkspaceOptions {
    params?: Record<string, string>;
    preservePath?: boolean;
    replace?: boolean;
    clearParams?: string[];
}

export interface RefreshWorkspaceOptions {
    silent?: boolean;
    skipEmptyRedirect?: boolean;
    preferredWorkspaceId?: string | null;
    throwOnError?: boolean;
}

function normalizeMembershipRole(role?: string | null): WorkspaceRole | null {
    const normalized = typeof role === 'string' ? role.toUpperCase() : '';
    if (!normalized) {
        return null;
    }
    return normalized === 'OWNER' ? 'OWNER' : 'MEMBER';
}

function syncWindowWorkspaceId(workspaceId: string | null) {
    window.Bustly = {
        ...(window.Bustly || {}),
        workspace_id: workspaceId || undefined,
    };
}

function applyWorkspaceCapability(
    workspace: Workspace,
    capability: WorkspaceCapabilityResponse | null | undefined,
): Workspace {
    const membershipRole = normalizeMembershipRole(capability?.membershipRole || workspace.role) || workspace.role;
    const workspaceRole = capability?.workspaceRoleName || capability?.workspaceRoleKey || capability?.role || null;
    const workspaceRoleKey = capability?.workspaceRoleKey || null;
    const workspaceRoleName = capability?.workspaceRoleName || null;

    if (
        workspace.role === membershipRole
        && workspace.membershipRole === membershipRole
        && workspace.workspaceRole === workspaceRole
        && workspace.workspaceRoleKey === workspaceRoleKey
        && workspace.workspaceRoleName === workspaceRoleName
    ) {
        return workspace;
    }

    return {
        ...workspace,
        role: membershipRole,
        membershipRole,
        workspaceRole,
        workspaceRoleKey,
        workspaceRoleName,
    };
}

export function normalizeProductWorkspace(workspace?: ProductWorkspace | null): Workspace | null {
    if (!workspace?.id) {
        return null;
    }

    const role = normalizeMembershipRole(workspace.currentUserRole || workspace.membershipRole || 'OWNER') || 'OWNER';
    return {
        id: workspace.id,
        name: workspace.name,
        logo_url: workspace.logoUrl || undefined,
        owner_id: workspace.ownerId,
        settings: workspace.settings || undefined,
        status: workspace.status as Workspace['status'],
        created_at: workspace.createdAt,
        updated_at: workspace.updatedAt,
        role,
        membershipRole: role,
        workspaceRole: workspace.workspaceRoleName || workspace.workspaceRoleKey || null,
        workspaceRoleKey: workspace.workspaceRoleKey || null,
        workspaceRoleName: workspace.workspaceRoleName || null,
        subscription: normalizeProductWorkspaceSubscription(workspace.subscription),
    };
}

async function resolvePluginWorkspaceBootstrap({
    currentSearch,
}: {
    currentSearch: string;
}): Promise<Workspace | null> {
    const resumeResponse = await authService.getPluginOnboardingWorkspaceResume();
    return resumeResponse?.shouldResume
        ? normalizeShopifyOnboardingResumeWorkspace(resumeResponse.workspace)
        : null;
}

export const useWorkspaces = (userId?: string): UseWorkspacesReturn => {
    const { get, set } = useUserStorage();
    const productCode = getCurrentProductCode();
    const currentWorkspaceStorageKey = getProductScopedStorageKey('currentWorkspaceId', productCode);
    const workspaceLoadScope = userId ? `${userId}:${productCode}` : null;
    const latestLoadScope = useRef(workspaceLoadScope);
    latestLoadScope.current = workspaceLoadScope;
    const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
    const [currentWorkspaceId, setCurrentWorkspaceId] = useState<string | null>(() => {
        const workspaceId = get(currentWorkspaceStorageKey);
        syncWindowWorkspaceId(workspaceId || null);
        return workspaceId || null;
    });
    const navigate = useNavigate();
    const location = useLocation();
    const latestReturnLocation = useRef(location);
    latestReturnLocation.current = location;
    const consumedWorkspaceParamRef = useRef<string | null>(null);
    const missingWorkspaceReturnRefreshRef = useRef<MissingWorkspaceReturnRefreshState | null>(null);
    const currentWorkspaceIdRef = useRef<string | null>(currentWorkspaceId);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const [missingWorkspaceReturnRetryTick, setMissingWorkspaceReturnRetryTick] = useState(0);
    const [completedWorkspaceLoadScope, setCompletedWorkspaceLoadScope] = useState<string | null>(null);

    const currentWorkspace = workspaces.find(w => w.id === currentWorkspaceId) || null;
    const isInitialWorkspaceLoadPending = Boolean(
        workspaceLoadScope && completedWorkspaceLoadScope !== workspaceLoadScope
    );

    useEffect(() => {
        if (!userId) return;
        try { retireCompletedOnboardingInstall(window.sessionStorage, { accountId: userId, productCode }, location.pathname, location.search); } catch {}
    }, [userId, productCode, location.pathname, location.search]);

    useEffect(() => {
        currentWorkspaceIdRef.current = currentWorkspaceId;
    }, [currentWorkspaceId]);

    const getPendingPaymentWorkspaceId = useCallback(() => {
        if (typeof window === 'undefined') return null;
        const searchParams = new URLSearchParams(window.location.search);
        return getPendingWorkspaceReturnId(searchParams, readPaymentReturnWorkspaceId());
    }, []);

    const _setCurrentWorkspaceId = useCallback((workspaceId: string | null) => {
        console.log("Setting current workspace ID:", workspaceId);
        currentWorkspaceIdRef.current = workspaceId;
        syncWindowWorkspaceId(workspaceId);
        set(currentWorkspaceStorageKey, workspaceId);
        setCurrentWorkspaceId(workspaceId);
    }, [currentWorkspaceStorageKey, set]);

    const applyWorkspaceSubscriptionPlan = useCallback((
        workspaceId: string,
        planCode?: string | null,
        planName?: string | null,
        billingCycle?: string | null,
    ) => {
        const normalizedWorkspaceId = workspaceId?.trim();
        if (!normalizedWorkspaceId) {
            return;
        }

        setWorkspaces(prevWorkspaces => prevWorkspaces.map(workspace => (
            workspace.id === normalizedWorkspaceId
                ? applyPaymentReturnPlanToWorkspace(workspace, planCode, planName, billingCycle)
                : workspace
        )));
    }, []);

    const fetchWorkspaces = useCallback(async (options?: RefreshWorkspaceOptions) => {
        if (!userId) {
            setWorkspaces([]);
            setCompletedWorkspaceLoadScope(null);
            return;
        }

        if (!options?.silent) {
            setIsLoading(true);
        }
        setError(null);
        let installRequestLocation: { pathname: string; search: string } | null = null;

        try {
            const currentSearch = typeof window !== 'undefined' ? window.location.search : '';
            const searchParams = new URLSearchParams(currentSearch);
            const currentPathname = typeof window !== 'undefined' ? window.location.pathname : '';
            const pendingPaymentWorkspaceId = getPendingPaymentWorkspaceId();
            const paymentReturnPlanOverride = readPaymentReturnPlanOverride(
                searchParams,
                readPaymentReturnWorkspaceId(),
            );
            const shouldRetryOnboardingReturn = Boolean(
                pendingPaymentWorkspaceId
                && shouldRetryMissingWorkspaceAfterOnboardingReturn(searchParams, currentPathname)
            );
            const suppressEmptyWorkspaceRedirect = shouldSuppressEmptyWorkspaceOnboardingRedirect(
                currentPathname,
                isPluginProduct(productCode) ? '' : currentSearch,
            );

            let formattedWorkspaces: Workspace[] = [];
            const installReturn = readBrowserOnboardingWorkspaceReturn({ accountId: userId, productCode }, currentPathname, currentSearch);
            if (installReturn?.scene === 'onboarding' && (installReturn.phase !== 'verified' || !pendingPaymentWorkspaceId)) {
                const returnLocation = latestReturnLocation.current;
                installRequestLocation = returnLocation;
                const exactWorkspace = await authService.getShopifyInstallWorkspace(installReturn.workspaceId, productCode);
                if (latestLoadScope.current !== workspaceLoadScope || latestReturnLocation.current.pathname !== returnLocation.pathname || latestReturnLocation.current.search !== returnLocation.search) return;
                assertInstallWorkspace(exactWorkspace, installReturn.workspaceId);
                const resumedWorkspace = normalizeShopifyOnboardingResumeWorkspace(exactWorkspace);
                if (!resumedWorkspace) throw new Error('Onboarding workspace unavailable');
                setWorkspaces([resumedWorkspace]);
                _setCurrentWorkspaceId(shouldKeepShopifyOnboardingResumeInactive(currentSearch, currentPathname) ? null : resumedWorkspace.id);
                return;
            }

            if (isPluginProduct(productCode)) {
                console.info('[useWorkspaces] Plugin workspace bootstrap:start', {
                    userId,
                    productCode,
                    currentWorkspaceId: currentWorkspaceIdRef.current,
                    pathname: currentPathname,
                    search: redactShopifyInstallTelemetry(currentSearch),
                    skipEmptyRedirect: Boolean(options?.skipEmptyRedirect),
                });
                let defaultWorkspace: Workspace | null = null;
                try {
                    const defaultWorkspaceResponse = await authService.getDefaultPluginWorkspace();
                    defaultWorkspace = normalizeProductWorkspace(defaultWorkspaceResponse);
                    console.info('[useWorkspaces] Plugin default workspace resolved', {
                        defaultWorkspaceId: defaultWorkspace?.id || null,
                    });
                } catch (defaultWorkspaceError) {
                    console.warn('[useWorkspaces] Plugin default workspace lookup failed; continuing with workspace list.', defaultWorkspaceError);
                }
                const pluginWorkspaceResponses = await authService.getPluginWorkspaces();
                formattedWorkspaces = pluginWorkspaceResponses
                    .map(normalizeProductWorkspace)
                    .filter((workspace): workspace is Workspace => Boolean(workspace));
                console.info('[useWorkspaces] Plugin workspace list resolved', {
                    count: formattedWorkspaces.length,
                    workspaceIds: formattedWorkspaces.map((workspace) => workspace.id),
                });

                setWorkspaces(formattedWorkspaces);

                const retryPluginOnboardingWorkspaceReturn = async (workspaceId: string): Promise<boolean> => {
                    if (shouldRetryOnboardingReturn && pendingPaymentWorkspaceId) {
                        const refreshState = missingWorkspaceReturnRefreshRef.current;
                        const attempts = refreshState?.workspaceId === workspaceId
                            ? refreshState.attempts
                            : 0;

                        if (refreshState?.workspaceId === workspaceId && refreshState.inFlight) {
                            return true;
                        }

                        if (attempts >= ONBOARDING_RETURN_MAX_ATTEMPTS) {
                            clearPaymentReturnWorkspaceId();
                            missingWorkspaceReturnRefreshRef.current = null;
                            const failureUrl = new URL('/admin/onboarding/failure', window.location.origin);
                            failureUrl.searchParams.set('workspace_id', workspaceId);
                            const planCode = searchParams.get('planCode');
                            const planName = searchParams.get('planName');
                            if (planCode) failureUrl.searchParams.set('planCode', planCode);
                            if (planName) failureUrl.searchParams.set('planName', planName);
                            window.location.replace(failureUrl.toString());
                            return true;
                        }

                        missingWorkspaceReturnRefreshRef.current = {
                            workspaceId,
                            attempts: attempts + 1,
                            inFlight: true,
                        };
                        await wait(ONBOARDING_RETURN_RETRY_DELAY_MS);
                        const currentRefreshState = missingWorkspaceReturnRefreshRef.current;
                        if (currentRefreshState?.workspaceId === workspaceId) {
                            missingWorkspaceReturnRefreshRef.current = {
                                ...currentRefreshState,
                                inFlight: false,
                            };
                            setMissingWorkspaceReturnRetryTick((tick) => tick + 1);
                        }
                        await fetchWorkspaces({
                            silent: true,
                            skipEmptyRedirect: true,
                            preferredWorkspaceId: workspaceId,
                        });
                        return true;
                    }

                    return false;
                };

                if (formattedWorkspaces.length > 0) {
                    const requestedPluginWorkspaceId = options?.preferredWorkspaceId?.trim()
                        || pendingPaymentWorkspaceId?.trim()
                        || null;
                    const requestedPluginWorkspaceExists = requestedPluginWorkspaceId
                        ? formattedWorkspaces.some((workspace) => workspace.id === requestedPluginWorkspaceId)
                        : false;
                    const currentWorkspaceStillValid = currentWorkspaceIdRef.current
                        ? formattedWorkspaces.some((workspace) => workspace.id === currentWorkspaceIdRef.current)
                        : false;
                    const defaultWorkspaceStillValid = defaultWorkspace
                        ? formattedWorkspaces.some((workspace) => workspace.id === defaultWorkspace.id)
                        : false;
                    const requestedWorkspaceSelection = resolvePostRefreshWorkspaceSelection({
                        workspaces: formattedWorkspaces,
                        currentWorkspaceId: currentWorkspaceIdRef.current,
                        pendingPaymentWorkspaceId,
                        preferredWorkspaceId: options?.preferredWorkspaceId || (installReturn?.scene === 'admin' ? installReturn.workspaceId : null),
                    });

                    if (requestedPluginWorkspaceId && !requestedPluginWorkspaceExists) {
                        console.info('[useWorkspaces] Plugin workspace bootstrap:requested workspace pending propagation', {
                            requestedPluginWorkspaceId,
                        });
                        if (await retryPluginOnboardingWorkspaceReturn(requestedPluginWorkspaceId)) {
                            return;
                        }
                        return;
                    }

                    const nextWorkspaceId = requestedWorkspaceSelection
                        || (currentWorkspaceStillValid
                            ? currentWorkspaceIdRef.current!
                            : defaultWorkspaceStillValid
                                ? defaultWorkspace!.id
                                : formattedWorkspaces[0].id);

                    _setCurrentWorkspaceId(nextWorkspaceId);
                    missingWorkspaceReturnRefreshRef.current = null;

                    if (!defaultWorkspaceStillValid || defaultWorkspace?.id !== nextWorkspaceId) {
                        await authService.setDefaultPluginWorkspace(nextWorkspaceId).catch((defaultError) => {
                            console.warn('[useWorkspaces] Failed to persist Plugin default workspace:', nextWorkspaceId, defaultError);
                        });
                    }
                    console.info('[useWorkspaces] Plugin workspace bootstrap:active workspace selected', {
                        nextWorkspaceId,
                        currentWorkspaceStillValid,
                        defaultWorkspaceStillValid,
                    });
                    return;
                }

                if (
                    pendingPaymentWorkspaceId
                    && await retryPluginOnboardingWorkspaceReturn(pendingPaymentWorkspaceId)
                ) {
                    return;
                }

                try {
                    console.info('[useWorkspaces] Plugin workspace bootstrap:checking onboarding resume');
                    const resumedWorkspace = await resolvePluginWorkspaceBootstrap({
                        currentSearch,
                    });

                    if (resumedWorkspace) {
                        _setCurrentWorkspaceId(null);
                        console.info(
                            '[useWorkspaces] Plugin workspace bootstrap:resuming onboarding workspace without activating it',
                            { workspaceId: resumedWorkspace.id },
                        );
                        const resumeDestination = getShopifyOnboardingResumeDestination(currentPathname, window.location.search, resumedWorkspace.id);
                        if (resumeDestination) navigate(resumeDestination);
                        return;
                    }
                } catch (resumeError) {
                    console.warn('[useWorkspaces] Failed to resolve Shopify onboarding resume; continuing normal onboarding flow.', resumeError);
                }

                _setCurrentWorkspaceId(null);
                console.info('[useWorkspaces] Plugin workspace bootstrap:no active or resumable workspace', {
                    shouldRedirectToOnboarding: shouldRedirectToOnboardingAfterRefresh({
                        workspaceCount: 0,
                        skipEmptyRedirect: options?.skipEmptyRedirect,
                        pendingPasswordSetup: isAuthPasswordSetupRoute(currentPathname) && hasPendingPasswordSetup(),
                        pendingWorkspaceReturn: shouldRetryOnboardingReturn,
                        suppressEmptyWorkspaceRedirect,
                    }),
                });
                if (shouldRedirectToOnboardingAfterRefresh({
                    workspaceCount: 0,
                    skipEmptyRedirect: options?.skipEmptyRedirect,
                    pendingPasswordSetup: isAuthPasswordSetupRoute(currentPathname) && hasPendingPasswordSetup(),
                    pendingWorkspaceReturn: shouldRetryOnboardingReturn,
                    suppressEmptyWorkspaceRedirect,
                })) {
                    navigateToOnboardingRoute(navigate, window.location.search, window.location.pathname);
                }
                return;
            }

            for (let attempt = 1; attempt <= (shouldRetryOnboardingReturn ? ONBOARDING_RETURN_MAX_ATTEMPTS : 1); attempt += 1) {
                const coreWorkspaceResponses = await authService.getCoreWorkspaces();
                const coreWorkspaces = coreWorkspaceResponses.map((workspace) => {
                    const subscription = workspace.subscription;
                    return {
                        ...workspace,
                        logo_url: workspace.logoUrl,
                        owner_id: workspace.ownerId,
                        created_at: workspace.createdAt,
                        updated_at: workspace.updatedAt,
                        workspace_members: [{
                            role: workspace.membershipRole || workspace.currentUserRole,
                            user_id: userId,
                            status: 'ACTIVE',
                        }],
                        workspace_subscriptions: subscription ? [{
                            id: subscription.id,
                            plan_id: subscription.planId,
                            status: subscription.status,
                            is_in_trial: subscription.isInTrial,
                            current_period_end: subscription.currentPeriodEnd,
                            trial_end_at: subscription.trialEndAt,
                            cancel_at_period_end: subscription.cancelAtPeriodEnd,
                            provider: subscription.provider,
                            created_at: subscription.createdAt,
                            benefit_plan: {
                                code: subscription.planCode,
                                name: subscription.planName,
                                tier: subscription.planTier,
                                billing_cycle: subscription.billingCycle,
                            },
                        }] : [],
                    };
                });
                const data = coreWorkspaces;

                formattedWorkspaces = data.map((item: any) => {
                    const matchedMember = item.workspace_members?.find(
                        (member: { user_id?: string; role?: WorkspaceRole; status?: string }) =>
                            member.user_id === userId && String(member.status || '').toUpperCase() === 'ACTIVE'
                    );
                    if (!matchedMember) return null;

                    const role = normalizeMembershipRole(matchedMember.role);
                    if (!role) return null;

                    const subscription = item.workspace_subscriptions && item.workspace_subscriptions.length > 0
                        ? (() => {
                            const rawSubscription = pickLatestWorkspaceSubscription(item.workspace_subscriptions as Array<any>);
                            if (!rawSubscription) return null;
                            const status = rawSubscription.status;
                            const currentPeriodEnd = rawSubscription.current_period_end;
                            const trialEndAt = rawSubscription.trial_end_at;
                            const now = Date.now();
                            const isTrial = isWorkspaceSubscriptionTrial({
                                status,
                                is_in_trial: rawSubscription.is_in_trial,
                                trial_end_at: trialEndAt,
                                plan_badge: rawSubscription.plan_badge,
                            });
                            const isExpired = Boolean(
                                (currentPeriodEnd && new Date(currentPeriodEnd).getTime() < now)
                                || status === 'expired'
                                || status === 'canceled'
                            );
                            const planName = rawSubscription.benefit_plan?.name || 'Basic';
                            const isExpiredTrial = isExpiredWorkspaceTrialSubscription({
                                status,
                                current_period_end: currentPeriodEnd,
                                trial_end_at: trialEndAt,
                                plan_label: isExpired ? 'Plan Expired' : planName,
                            });
                            const isCanceled = Boolean(rawSubscription.cancel_at_period_end);

                            let statusLabel = planName;
                            let statusMessage = '';
                            let actionLabel = 'Manage';
                            let planBadge = '';

                            if (isTrial) {
                                planBadge = 'Trial';
                                statusLabel = 'Trial';
                                statusMessage = trialEndAt
                                    ? `${isCanceled ? 'Expires' : 'Will renew'} on ${new Date(trialEndAt).toLocaleDateString('en-US')}.`
                                    : '';
                                actionLabel = isCanceled ? 'Renew' : 'Start subscription';
                            } else if (isExpired || status === 'expired' || status === 'canceled') {
                                statusLabel = 'Plan Expired';
                                statusMessage = isExpiredTrial
                                    ? `Free trial expired on ${new Date(trialEndAt).toLocaleDateString('en-US')}.`
                                    : currentPeriodEnd
                                        ? `Expired on ${new Date(currentPeriodEnd).toLocaleDateString('en-US')}.`
                                        : 'Plan expired.';
                                actionLabel = isExpiredTrial ? 'Start subscription' : 'Upgrade';
                            } else if (isCanceled && currentPeriodEnd) {
                                const daysLeft = Math.max(0, Math.ceil((new Date(currentPeriodEnd).getTime() - now) / (1000 * 60 * 60 * 24)));
                                statusMessage = daysLeft < 7
                                    ? `${daysLeft} days left.`
                                    : `Expires on ${new Date(currentPeriodEnd).toLocaleDateString('en-US')}.`;
                                actionLabel = 'Renew';
                            } else if (currentPeriodEnd) {
                                statusMessage = `Will renew on ${new Date(currentPeriodEnd).toLocaleDateString('en-US')}.`;
                                actionLabel = 'Manage';
                            }

                            return {
                                id: rawSubscription.id,
                                plan_id: rawSubscription.plan_id,
                                status,
                                is_in_trial: rawSubscription.is_in_trial,
                                current_period_end: currentPeriodEnd,
                                trial_end_at: trialEndAt,
                                cancel_at_period_end: rawSubscription.cancel_at_period_end,
                                created_at: rawSubscription.created_at,
                                provider: rawSubscription.provider || null,
                                plan_code: rawSubscription.benefit_plan?.code || 'basic',
                                plan_name: planName,
                                plan_tier: rawSubscription.benefit_plan?.tier || 'free',
                                billing_cycle: rawSubscription.benefit_plan?.billing_cycle || 'monthly',
                                plan_label: isExpired ? 'Plan Expired' : planName,
                                plan_badge: planBadge,
                                status_label: statusLabel,
                                status_message: statusMessage,
                                action_label: actionLabel,
                                next_action: actionLabel,
                            } as WorkspaceSubscription;
                        })()
                        : null;

                    const { workspace_members, workspace_subscriptions, ...workspace } = item;
                    return {
                        ...workspace,
                        role,
                        membershipRole: role,
                        workspaceRole: null,
                        workspaceRoleKey: null,
                        workspaceRoleName: null,
                        subscription
                    };
                }).filter((workspace): workspace is Workspace => Boolean(workspace));

                if (paymentReturnPlanOverride) {
                    formattedWorkspaces = formattedWorkspaces.map((workspace) => (
                        shouldApplyPaymentReturnPlanOverride({
                            override: paymentReturnPlanOverride,
                            workspaceId: workspace.id,
                        })
                            ? applyPaymentReturnPlanToWorkspace(
                                workspace,
                                paymentReturnPlanOverride.planCode,
                                paymentReturnPlanOverride.planName,
                                paymentReturnPlanOverride.billingCycle,
                            )
                            : workspace
                    ));
                }

                setWorkspaces(formattedWorkspaces);

                const hasPendingWorkspace = pendingPaymentWorkspaceId
                    ? formattedWorkspaces.some((workspace) => workspace.id === pendingPaymentWorkspaceId)
                    : false;

                if (!shouldRetryOnboardingReturn || hasPendingWorkspace) {
                    break;
                }

                if (attempt < ONBOARDING_RETURN_MAX_ATTEMPTS) {
                    await wait(ONBOARDING_RETURN_RETRY_DELAY_MS);
                }
            }

            if (
                shouldRetryOnboardingReturn
                && pendingPaymentWorkspaceId
                && !formattedWorkspaces.some((workspace) => workspace.id === pendingPaymentWorkspaceId)
            ) {
                clearPaymentReturnWorkspaceId();
                const failureUrl = new URL('/admin/onboarding/failure', window.location.origin);
                failureUrl.searchParams.set('workspace_id', pendingPaymentWorkspaceId);
                const planCode = searchParams.get('planCode');
                const planName = searchParams.get('planName');
                if (planCode) failureUrl.searchParams.set('planCode', planCode);
                if (planName) failureUrl.searchParams.set('planName', planName);
                window.location.replace(failureUrl.toString());
                return;
            }

            if (installReturn?.scene !== 'admin' && shouldResolveShopifyOnboardingResume({
                currentSearch,
                workspaceCount: formattedWorkspaces.length,
                hasPendingPaymentWorkspace: Boolean(pendingPaymentWorkspaceId),
                currentPathname,
            })) {
                try {
                    const resumeResponse = await authService.getPluginOnboardingWorkspaceResume();
                    const resumedWorkspace = resumeResponse?.shouldResume
                        ? normalizeShopifyOnboardingResumeWorkspace(resumeResponse.workspace)
                        : null;

                    if (resumedWorkspace) {
                        if (shouldKeepShopifyOnboardingResumeInactive(currentSearch, currentPathname)) {
                            _setCurrentWorkspaceId(null);
                            navigate(buildShopifyOnboardingResumeRoute(window.location.search, resumedWorkspace.id));
                            return;
                        }

                        const nextWorkspaces = formattedWorkspaces.some(
                            (workspace) => workspace.id === resumedWorkspace.id,
                        )
                            ? formattedWorkspaces
                            : [resumedWorkspace, ...formattedWorkspaces];
                        setWorkspaces(nextWorkspaces);
                        _setCurrentWorkspaceId(resumedWorkspace.id);
                        const resumeDestination = getShopifyOnboardingResumeDestination(currentPathname, window.location.search, resumedWorkspace.id);
                        if (resumeDestination) navigate(resumeDestination);
                        return;
                    }
                } catch (resumeError) {
                    console.warn('[useWorkspaces] Failed to resolve Shopify onboarding resume; continuing normal onboarding flow.', resumeError);
                }
            }

            if (
                currentWorkspaceIdRef.current
                && !formattedWorkspaces.some((workspace) => workspace.id === currentWorkspaceIdRef.current)
                && !pendingPaymentWorkspaceId
            ) {
                if (formattedWorkspaces.length > 0) {
                    const fallbackWorkspaceId = formattedWorkspaces[0].id;
                    console.warn(
                        '[useWorkspaces] Current workspace not found in fetched list, falling back to first workspace:',
                        currentWorkspaceIdRef.current,
                        '->',
                        fallbackWorkspaceId
                    );
                    _setCurrentWorkspaceId(fallbackWorkspaceId);
                } else {
                    console.warn(
                        '[useWorkspaces] Current workspace is outside the fetched product scope; clearing selection:',
                        currentWorkspaceIdRef.current
                    );
                    _setCurrentWorkspaceId(null);
                }
            }

            if (installReturn?.scene === 'admin' && !formattedWorkspaces.some(workspace => workspace.id === installReturn.workspaceId)) {
                throw new Error('Shopify return workspace unavailable');
            }
            const nextWorkspaceId = resolvePostRefreshWorkspaceSelection({
                workspaces: formattedWorkspaces,
                currentWorkspaceId: currentWorkspaceIdRef.current,
                pendingPaymentWorkspaceId,
                preferredWorkspaceId: options?.preferredWorkspaceId || (installReturn?.scene === 'admin' ? installReturn.workspaceId : null),
            });

            if (nextWorkspaceId) {
                console.log('刷新后设置当前 workspace:', nextWorkspaceId);
                _setCurrentWorkspaceId(nextWorkspaceId);
            }

            if (shouldRedirectToOnboardingAfterRefresh({
                workspaceCount: formattedWorkspaces.length,
                skipEmptyRedirect: options?.skipEmptyRedirect,
                pendingPasswordSetup: isAuthPasswordSetupRoute(currentPathname) && hasPendingPasswordSetup(),
                pendingWorkspaceReturn: shouldRetryOnboardingReturn,
                suppressEmptyWorkspaceRedirect,
            })) {
                navigateToOnboardingRoute(navigate, window.location.search, window.location.pathname);
            }
        } catch (err: any) {
            if (latestLoadScope.current !== workspaceLoadScope || (installRequestLocation && (latestReturnLocation.current.pathname !== installRequestLocation.pathname || latestReturnLocation.current.search !== installRequestLocation.search))) return;
            console.error('Error fetching workspaces:', err);
            setError(err);
            if (options?.throwOnError) {
                throw err;
            }
        } finally {
            if (latestLoadScope.current === workspaceLoadScope) {
                setCompletedWorkspaceLoadScope(workspaceLoadScope);
                if (!options?.silent) setIsLoading(false);
            }
        }
        }, [userId, _setCurrentWorkspaceId, navigate, getPendingPaymentWorkspaceId, productCode, workspaceLoadScope]);

    useEffect(() => {
        if (!userId) return;
        fetchWorkspaces();
    }, [userId, fetchWorkspaces]);

    useEffect(() => {
        const workspaceId = currentWorkspace?.id;
        if (!userId || !workspaceId) return;

        let cancelled = false;

        void featureService.getWorkspaceCapabilities(workspaceId)
            .then((capability) => {
                if (cancelled) return;
                setWorkspaces(prevWorkspaces => prevWorkspaces.map((workspace) => (
                    workspace.id === workspaceId
                        ? applyWorkspaceCapability(workspace, capability)
                        : workspace
                )));
            })
            .catch((capabilityError) => {
                if (cancelled) return;
                console.warn('[useWorkspaces] Failed to enrich current workspace role from capabilities:', workspaceId, capabilityError);
            });

        return () => {
            cancelled = true;
        };
    }, [userId, currentWorkspace?.id]);

    const switchWorkspace = useCallback((workspaceId: string, options?: SwitchWorkspaceOptions | Record<string, string>) => {
        const normalizedOptions: SwitchWorkspaceOptions = (
            options && !('params' in options) && !('preservePath' in options) && !('replace' in options) && !('clearParams' in options)
        )
            ? { params: options as Record<string, string> }
            : (options as SwitchWorkspaceOptions | undefined) || {};

        const {
            params,
            preservePath = false,
            replace = false,
            clearParams = [],
        } = normalizedOptions;

        console.log("Switching to workspace:", workspaceId, normalizedOptions);
        _setCurrentWorkspaceId(workspaceId);

        const nextSearchParams = new URLSearchParams(preservePath ? location.search : '');
        const nextPathname = preservePath ? location.pathname : '/';
        const nextHash = preservePath ? location.hash : '';

        clearParams.forEach((key) => {
            nextSearchParams.delete(key);
        });

        if (params) {
            Object.entries(params).forEach(([key, value]) => {
                nextSearchParams.set(key, value);
            });
        }

        const nextSearch = nextSearchParams.toString();
        navigate({
            pathname: nextPathname,
            search: nextSearch ? `?${nextSearch}` : '',
            hash: nextHash,
        }, { replace });
    }, [_setCurrentWorkspaceId, location.hash, location.pathname, location.search, navigate]);

    const setCurrentWorkspace = useCallback((workspaceId: string | null) => {
        _setCurrentWorkspaceId(workspaceId);
    }, [_setCurrentWorkspaceId]);

    useEffect(() => {
        if (!userId || isLoading || workspaces.length === 0 || isFullPageShopifyReturn(location.search)) return;

        const searchParams = new URLSearchParams(location.search);
        const requestedWorkspaceId = getPendingWorkspaceReturnId(
            searchParams,
            readPaymentReturnWorkspaceId()
        ) || getRequestedWorkspaceIdFromSearchParams(searchParams);
        const isSuccessfulReturn = isSuccessfulWorkspaceReturn(searchParams);
        const shouldPreservePaymentResultParams = shouldPreserveWorkspaceReturnParamsForPaymentResult(searchParams);
        const returnParamsToClear = ['workspace_id', 'workspaceId'];

        const clearReturnParamsFromUrl = (nextUrl: URL) => {
            returnParamsToClear.forEach((param) => {
                nextUrl.searchParams.delete(param);
            });
        };

        if (!requestedWorkspaceId) {
            consumedWorkspaceParamRef.current = null;
            missingWorkspaceReturnRefreshRef.current = null;
            return;
        }

        if (consumedWorkspaceParamRef.current === requestedWorkspaceId) {
            return;
        }

        consumedWorkspaceParamRef.current = requestedWorkspaceId;

        const targetWorkspace = workspaces.find((workspace) => workspace.id === requestedWorkspaceId);
        if (!targetWorkspace) {
            // Payment success may arrive before the target workspace has propagated
            // into the refreshed workspace list. Keep waiting instead of dropping
            // the requested workspace and falling back to another one.
            if (isSuccessfulReturn) {
                consumedWorkspaceParamRef.current = null;
                const refreshState = missingWorkspaceReturnRefreshRef.current;
                const attempts = refreshState?.workspaceId === requestedWorkspaceId
                    ? refreshState.attempts
                    : 0;

                if (refreshState?.workspaceId === requestedWorkspaceId && refreshState.inFlight) {
                    return;
                }

                if (attempts >= MISSING_WORKSPACE_RETURN_MAX_REFRESH_ATTEMPTS) {
                    console.error(
                        '[useWorkspaces] Missing successful return workspace after bounded refresh attempts; clearing workspace return params:',
                        requestedWorkspaceId
                    );
                    clearPaymentReturnWorkspaceId();
                    missingWorkspaceReturnRefreshRef.current = null;
                    const nextUrl = new URL(window.location.href);
                    clearReturnParamsFromUrl(nextUrl);
                    navigate({
                        pathname: stripAdminBasename(nextUrl.pathname),
                        search: nextUrl.search,
                        hash: nextUrl.hash,
                    }, { replace: true });
                    return;
                }

                missingWorkspaceReturnRefreshRef.current = {
                    workspaceId: requestedWorkspaceId,
                    attempts: attempts + 1,
                    inFlight: true,
                };

                void wait(attempts === 0 ? 0 : MISSING_WORKSPACE_RETURN_RETRY_DELAY_MS)
                    .then(() => {
                        const latestSearchParams = new URLSearchParams(window.location.search);
                        const latestRequestedWorkspaceId = getPendingWorkspaceReturnId(
                            latestSearchParams,
                            readPaymentReturnWorkspaceId()
                        ) || getRequestedWorkspaceIdFromSearchParams(latestSearchParams);

                        if (latestRequestedWorkspaceId !== requestedWorkspaceId) {
                            missingWorkspaceReturnRefreshRef.current = null;
                            return undefined;
                        }

                        return fetchWorkspaces({
                            silent: true,
                            skipEmptyRedirect: true,
                            preferredWorkspaceId: requestedWorkspaceId,
                        });
                    })
                    .finally(() => {
                        const currentRefreshState = missingWorkspaceReturnRefreshRef.current;
                        if (currentRefreshState?.workspaceId === requestedWorkspaceId) {
                            missingWorkspaceReturnRefreshRef.current = {
                                ...currentRefreshState,
                                inFlight: false,
                            };
                            setMissingWorkspaceReturnRetryTick((tick) => tick + 1);
                        }
                    });
                return;
            }

            missingWorkspaceReturnRefreshRef.current = null;
            console.warn('[useWorkspaces] Ignoring unknown workspace_id from URL:', requestedWorkspaceId);
            clearPaymentReturnWorkspaceId();
            const nextUrl = new URL(window.location.href);
            nextUrl.searchParams.delete('workspace_id');
            nextUrl.searchParams.delete('workspaceId');
            navigate({
                pathname: stripAdminBasename(nextUrl.pathname),
                search: nextUrl.search,
                hash: nextUrl.hash,
            }, { replace: true });
            return;
        }

        missingWorkspaceReturnRefreshRef.current = null;

        if (requestedWorkspaceId === currentWorkspaceId) {
            clearPaymentReturnWorkspaceId();
            if (shouldPreservePaymentResultParams) {
                return;
            }
            const nextUrl = new URL(window.location.href);
            clearReturnParamsFromUrl(nextUrl);
            navigate({
                pathname: stripAdminBasename(nextUrl.pathname),
                search: nextUrl.search,
                hash: nextUrl.hash,
            }, { replace: true });
            return;
        }

        if (!currentWorkspaceId) {
            _setCurrentWorkspaceId(requestedWorkspaceId);
            clearPaymentReturnWorkspaceId();
            if (shouldPreservePaymentResultParams) {
                return;
            }
            const nextUrl = new URL(window.location.href);
            clearReturnParamsFromUrl(nextUrl);
            navigate({
                pathname: stripAdminBasename(nextUrl.pathname),
                search: nextUrl.search,
                hash: nextUrl.hash,
            }, { replace: true });
            return;
        }

        clearPaymentReturnWorkspaceId();
        switchWorkspace(requestedWorkspaceId, {
            preservePath: true,
            replace: true,
            clearParams: shouldPreservePaymentResultParams ? [] : returnParamsToClear,
        });
    }, [
        userId,
        isLoading,
        workspaces,
        location.search,
        currentWorkspaceId,
        switchWorkspace,
        _setCurrentWorkspaceId,
        fetchWorkspaces,
        navigate,
        missingWorkspaceReturnRetryTick,
    ]);

    return {
        workspaces,
        currentWorkspace,
        isLoading: isLoading || isInitialWorkspaceLoadPending,
        error,
        refreshWorkspaces: fetchWorkspaces,
        switchWorkspace,
        setCurrentWorkspace,
        applyWorkspaceSubscriptionPlan,
    };
};
